<template>
  <div class="my-4">
    <v-card v-if="eventsDisplay && userMessage != null" class="msg my-2">
      <v-btn
        v-if="allSteps.completed"
        class="expand-btn"
        :icon="show ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        @click="show = !show" variant="text" size="x-large"
      ></v-btn>
      <v-card-title class="mb-n4">
        <span v-if="!allSteps.completed">Analyzing {{ userMessage }}...</span>
        <span v-else @click="show = !show" class="clickable d-block mr-8">Analysis of {{ userMessage }} completed in {{ getTimeElapsed(allSteps.started, allSteps.finished) }}</span>
      </v-card-title>
      <v-card-text>
        <v-expand-transition>
          <div v-if="show" class="mt-6">
            <p v-if="agentEvents.length === 0 && agentMessageFormatted.length > 0">
              <v-icon color ="green" icon="mdi-check-circle"></v-icon>
              All events completed
            </p>
            <ul v-else-if="agentEvents.length > 0" class="events-list">
              <li v-for="{ name, completed, started, finished } of agentEvents" :key="`${name}-${completed}-${finished}`">
                <v-icon v-if="completed" color ="green" icon="mdi-check-circle"></v-icon>
                <v-icon v-else color="orange" icon="mdi-progress-clock"></v-icon>
                {{ getEventLabel(name, completed) }}
                <span v-if="!completed" class="text-caption pl-2">{{ getTime(started) }}</span>
                <span v-else class="text-caption pl-2">({{ getTimeElapsed(started, finished) }})</span>
              </li>
            </ul>
            <p class="mt-6">
              <strong>Keep this page open until the analysis completes.</strong> It typically takes 2 minutes or less.<br/>
              If you have feedback or suggestions please send to <a href="mailto:feedback@stonkreport.com" target="_blank">feedback@stonkreport.com</a> 🙏
            </p>
          </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>
    <!-- <div v-if="eventsDisplay && userMessage != null" class="msg my-2">
    </div> -->
    <v-card v-else class="msg my-2">
      <v-card-text>
        <div class="user-icon"><span class="user-letter">{{ userLetter }}</span></div>
        <p class="user-msg mt-2 mb-2">{{ userMessage }}</p>
      </v-card-text>
    </v-card>
    <v-card class="msg">
      <v-card-text>
        <div v-if="agentMessageFormatted !== ''">
          <p class="text-overline" v-if="timestamp">{{ getTime(timestamp) }}</p>
          <pre ref="pre"><code class="chat-text language-markdown" v-html="agentMessageFormatted"></code></pre>
        </div>
        <div v-else class="chat-bubble">
          <div class="typing">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>
<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { marked } from 'marked'
import Prism from 'prismjs'
import 'prismjs/themes/prism-okaidia.css'
import { mapState } from 'vuex'
import { hash } from '@/utils/general'
import { StepsMap } from '@/store/index'
import { AllNews } from '@/utils/api'
import Code from './Code.vue'

@Options({
  props: {
    id: String,
    userMessage: String,
    agentMessage: String,
    showEvents: Boolean,
    timestamp: Number,
    symbol: String
  },
  computed: {
    ...mapState(['username', 'firstname', 'steps']),
  },
  components: {
    Code
  }
})
export default class Analysis extends Vue {
  id!: string
  username!: string
  firstname!: string
  userMessage!: string
  userMsgHash: string = ''
  agentMessage!: string
  agentMsgHash: string = ''
  userMessageFormatted: string = ''
  agentMessageFormatted: string = ''
  timestamp!: number

  showEvents!: boolean
  steps!: Record<string, StepsMap>
  allSteps: { completed: boolean, started: number, finished: number } = { completed: false, started: 0, finished: 0 }
  show = true

  get eventsDisplay(): boolean {
    if (this.showEvents == null) {
      return false
    }
    return this.showEvents
  }

  public getEventLabel(name: string, complete: boolean) {
    switch (name) {
      case 'quote':
        return complete ? 'Got quote.' : 'Getting quote..'
      case 'incomeStatement':
        return complete ? 'Analyzed income statements.' : 'Analyzing income statements..'
      case 'cashflowStatement':
        return complete ? 'Analyzed cashflow statements.' : 'Analyzing cashflow statements..'
      case 'balanceSheet':
        return complete ? 'Analyzed balance sheets.' : 'Analyzing balance sheets..'
      case 'calendarEvents':
        return complete ? 'Got calendar events.' : 'Getting calendar events..'
      case 'companySummary':
        return complete ? 'Got company summary.' : 'Getting company summary..'
      case 'newsSentiments':
        return complete ? 'Got recent news & sentiment.' : 'Getting recent news & sentiment..'
      case 'insights':
        return complete ? 'Got financial insights.' : 'Getting financial insights..'
      case 'assetType':
        return complete ? 'Got asset type.' : 'Getting asset type..'
      case 'assetData':
        return complete ? 'Got asset data.' : 'Getting asset data..'
      case 'analysis':
        return complete ? 'Analyzed asset.' : 'Analyzing asset..'
      case 'analysisSummary':
        return complete ? 'Got analysis summary.' : 'Summarizing analysis...'
    }
    return name
  }

  public getTime(timestamp: number) {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
  }

  public getTimeElapsed(start: number, finished: number) {
    const sec = (finished - start) / 1000
    const suffix = sec > 60 ? 'mins' : 'secs'
    if (sec > 60) {
      return (sec / 60).toFixed(1) + ' ' + suffix
    }
    return sec.toFixed(1) + ' ' + suffix
  }

  public analyzeSteps(steps: { completed: boolean, started: number, finished: number }[]) {
    // const entries = Object.values(steps)
    
    if (steps.length === 0) {
      return {
        started: 0,
        finished: 0,
        completed: false
      }
    }

    const started = Math.min(...steps.map(step => step.started))
    const finished = Math.max(...steps.map(step => step.finished))
    const completed = steps.every(step => step.completed)
    this.show = !completed
    this.allSteps = {
      started,
      finished,
      completed
    }
  }

  get agentEvents() {
    if (this.steps == null || this.steps[this.id] == null) {
      return []
    }
    return Object.keys(this.steps[this.id])
      .map(key => ({ name: key, ...this.steps[this.id][key] }))
      .sort((a, b) =>  (a.finished === 0 ? a.started : a.finished) - (b.finished === 0 ? b.started : b.finished))
  }

  get usernameDisplay(): string {
    if (this.firstname == null || this.firstname == '') {
      return this.username
    } else if (this.firstname != null && this.firstname != '') {
      return this.firstname 
    }
    return this.username
  }

  get userLetter() {
    if (this.usernameDisplay != null && this.usernameDisplay.trim().length > 0) {
      return this.usernameDisplay.charAt(0).toUpperCase()
    }
    return '?'
  }

  async mounted() {
    this.analyzeSteps(this.agentEvents)
    await this.updateMessages()
  }

  async updated() {
    this.analyzeSteps(this.agentEvents)
    await this.updateMessages()
  }

  async updateMessages() {
    const agentMsgHash = hash(this.agentMessage)
    const userMsgHash = hash(this.userMessage)
    if ((this.agentMessage != null && this.agentMessage !== '') && (agentMsgHash !== this.agentMsgHash || userMsgHash !== this.userMsgHash)) {
      this.agentMessageFormatted = await this.formatMarkdown(this.agentMessage)
      this.agentMsgHash = agentMsgHash
      this.userMsgHash = userMsgHash
      console.log('Messages updated')
    }
  }

  async formatMarkdown(text: string): Promise<string> {
    return marked.parse(text)
  }
}
</script>
<style scoped lang="scss">
  .clickable {
    cursor: pointer;
  }
  
  .expand-btn {
      position: absolute;
      top: 0;
      right: 0;
      margin: 10px;
    }
  .events-list {
    li {
      list-style: none;
      // padding-left: 20px;
    }
  }
  .user-icon {
    width: 30px;
    height: 30px;
    background: rgb(var(--v-theme-primary));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &.smaller {
      width: 27px;
      height: 27px;

      .user-letter {
        font-size: 14px;
      }
    }

    .user-letter {
      color: rgb(var(--v-theme-surface));
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 16px;
      font-weight: bold;
      // font-weight: 500;
      line-height: 1;
      text-transform: uppercase;
      user-select: none;
    }
  }
  .chat-bubble {
    // background: #e6e6e6;
    // top: -30px;
    margin-left: -20px;
    border-radius: 15px;
    padding: 15px 25px;
    display: inline-block;
    position: relative;

    .typing {
      display: flex;
      gap: 4px;
      align-items: center;
      height: 17px;
    }

    .dot {
      width: 7px;
      height: 7px;
      background: #757575;
      border-radius: 50%;
      animation: bounce 1.8s linear infinite;
    }

    .dot:nth-child(2) {
      animation-delay: 0.3s;
    }

    .dot:nth-child(3) {
      animation-delay: 0.6s;
    }
  }

  @keyframes bounce {
    0%, 40%, 100% {
      transform: translateY(0);
    }
    20% {
      transform: translateY(-8px);
    }
  }

  .user-msg {
    display: block;
  }

  .msg {
    border: 1px solid rgb(133, 133, 133);
    border-radius: 8px;
    padding: 20px;
  }

  // code[class*="language-"] > ul:not(:first-child),
  // code[class*="language-"] > ol:not(:first-child) {
  //   padding-left: 40px;
  // }
  code[class*="language-"] > ul,
  code[class*="language-"] > ol {
    padding-left: 40px;
  }

  code[class*="language-"] > hr:first-child {
    display: none !important;
    opacity: 0;
  }

  pre[class*="language-" ],
  code[class*="language-"] {
    background: #02131f;
    box-sizing: border-box;
    font-family: Avenir, Helvetica, Arial, sans-serif;
    font-size: 18px;
    overflow: auto;
    white-space: normal;
    word-break: break-word;

    ol {
      margin-left: 40px;
    }
    ul {
      margin-left: 40px;
    }
    dl {
      margin-left: 40px;
    }

    &:deep(ul, ol, dl) {
      padding-left: 40px;
      // padding-left: 1rem;
      list-style-position: outside;
    }
  }
</style>